/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, lazy, Suspense, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Hero from "./components/Hero";
import ArchetypeSelector from "./components/ArchetypeSelector";
import RoyalFooter from "./components/RoyalFooter";
import Header from "./components/Header";
import PageWrapper from "./components/PageWrapper";
import NotFound from "./components/NotFound";
import { BRAND_IMAGES } from "./data";
import { useLanguage } from "./context/LanguageContext";
import { getOptimizedImageUrl } from "./utils/image";
import { useMeditationSound } from "./hooks/useMeditationSound";
import { ArchetypeId } from "./types";

const YouTubeFeed = lazy(() => import("./components/YouTubeFeed"));
const ArticlesAndArchetypes = lazy(() => import("./components/ArticlesAndArchetypes"));
const ArchetypeQuiz = lazy(() => import("./components/ArchetypeQuiz"));
const MeditationExperience = lazy(() => import("./components/MeditationExperience"));
const HaftKhanJourney = lazy(() => import("./components/HaftKhanJourney"));
const HafezOracle = lazy(() => import("./components/HafezOracle"));

export default function App() {
  const { lang, setLang, isRtl, t, localized } = useLanguage();
  const [selectedArchetypeId, setSelectedArchetypeId] = useState<ArchetypeId | null>(null);
  const [meditationMode, setMeditationMode] = useState<boolean>(false);
  const [isHafezOpen, setIsHafezOpen] = useState<boolean>(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Meditation sound hook
  const { isAudioPlaying, toggleMeditationSound, stopMeditationSound } = useMeditationSound();

  // Determine active view based on current path. Strip the optional /en
  // prefix so /en/articles/foo and /articles/foo both map to "articles".
  const pathNoLocale = location.pathname.replace(/^\/en(?=\/|$)/, "");
  let activeView: "home" | "videos" | "articles" | "quiz" | "haftkhan" = "home";
  if (pathNoLocale.startsWith("/articles")) {
    activeView = "articles";
  } else if (pathNoLocale.startsWith("/videos")) {
    activeView = "videos";
  } else if (pathNoLocale.startsWith("/quiz")) {
    activeView = "quiz";
  } else if (pathNoLocale.startsWith("/haftkhan")) {
    activeView = "haftkhan";
  }

  // Helper: derive the current view's localized path so deep links stay in the
  // active locale. E.g. on `/en/articles/foo`, `activeView` is `articles` but
  // the real route is `/en/articles/foo`.
  const localizedPath = useCallback((view: string, slug?: string) => {
    const base = view === "home" ? "/" : `/${view}`;
    const target = slug ? `${base}/${slug}` : base;
    return localized(target);
  }, [localized]);

  // Handle direct deep linking to articles on page mount (legacy URL support)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const articleSlug = params.get("article");
    if (articleSlug) {
      navigate(localizedPath("articles", articleSlug), { replace: true });
    }
  }, [navigate, localizedPath]);

  // Scroll to top when activeView changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeView]);

  // Handle automatic scrolling/routing when an archetype is selected
  const handleArchetypeSelect = (archetypeId: ArchetypeId) => {
    setSelectedArchetypeId(archetypeId);
  };

  const handleClearArchetype = () => {
    setSelectedArchetypeId(null);
  };

  // Turn off synth if meditation mode is explicitly turned off
  useEffect(() => {
    if (!meditationMode && isAudioPlaying) {
      stopMeditationSound();
    }
  }, [meditationMode, isAudioPlaying, stopMeditationSound]);

  const handleNavClick = useCallback((view: string) => {
    const target = `/${view === "home" ? "" : view}`;
    navigate(lang === "en" ? (target === "/" ? "/en" : `/en${target}`) : target);
    setIsMobileMenuOpen(false);
  }, [navigate, lang]);

  const handleToggleLang = useCallback(() => {
    setLang(lang === "fa" ? "en" : "fa");
  }, [lang, setLang]);

  const handleToggleMeditationMode = useCallback(() => {
    setMeditationMode((prev) => !prev);
  }, []);

  const handleOpenHafez = useCallback(() => {
    setIsHafezOpen(true);
  }, []);

  const handleToggleMeditationSound = useCallback(() => {
    toggleMeditationSound();
  }, [toggleMeditationSound]);

  return (
    <div dir={isRtl ? "rtl" : "ltr"} className="min-h-screen bg-[#121212] flex flex-col relative overflow-x-hidden">
      {/* Floating Golden Particles (Only visible in Meditation Mode) */}
      <AnimatePresence>
        {meditationMode && (
          <>
            <div className="fixed inset-0 pointer-events-none z-30 overflow-hidden">
              {[...Array(12)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1 h-1 bg-amber-400 rounded-full opacity-60"
                  style={{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                  }}
                  animate={{
                    y: [0, -100, 0],
                    x: [0, Math.random() * 30 - 15, 0],
                    scale: [1, 1.8, 1],
                    opacity: [0.3, 0.8, 0.3],
                  }}
                  transition={{
                    duration: 8 + Math.random() * 8,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.4,
                  }}
                />
              ))}
            </div>
            <Suspense fallback={null}>
              <MeditationExperience onClose={() => setMeditationMode(false)} onOpenHafez={() => setIsHafezOpen(true)} />
            </Suspense>
          </>
        )}
      </AnimatePresence>

      {/* Main Content Wrapper (Receives CSS filter styling for a cinematic dimmed state during meditation) */}
      <div className={`flex-grow flex flex-col transition-all duration-1000 ${
        meditationMode ? "bg-[#0a0a0a] brightness-[0.7] saturate-[0.8]" : "bg-[#121212]"
      }`}>

        {/* Header Component */}
        <Header
          meditationMode={meditationMode}
          isAudioPlaying={isAudioPlaying}
          isHafezOpen={isHafezOpen}
          activeView={activeView}
          onNavigate={handleNavClick}
          onToggleLang={handleToggleLang}
          onToggleMeditationSound={handleToggleMeditationSound}
          onToggleMeditationMode={handleToggleMeditationMode}
          onOpenHafez={handleOpenHafez}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          isMobileMenuOpen={isMobileMenuOpen}
        />

        {/* Main Multi-page Portal Structure */}
        <main className="flex-grow">
          <Suspense fallback={
            <div className="min-h-[60vh] flex flex-col items-center justify-center text-amber-200">
              <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mb-4"></div>
              <span className="font-sans text-[11px] text-amber-500/80 tracking-widest uppercase">Loading Portal...</span>
            </div>
          }>
            <Routes>
              <Route path="/" element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {/* Section 1: Hero Intro (Great Hall) */}
                  <Hero />

                  {/* Section 2: YouTube Feed (Featured/Recent 3 Videos in compact mode) */}
                  <Suspense fallback={
                    <div className="w-full min-h-[500px] flex items-center justify-center border-y border-amber-500/5 bg-[#080604]/40">
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <span className="text-xs text-amber-500/60 font-mono tracking-widest uppercase">در حال فراخوانی نگاره‌های ویدیویی...</span>
                      </div>
                    </div>
                  }>
                    <YouTubeFeed
                      isHomePreview={true}
                      onViewAll={() => navigate(localizedPath("videos"))}
                    />
                  </Suspense>

                  {/* Section 3: Articles / Inscriptions (Featured 3 Inscriptions in compact mode - No images) */}
                  <Suspense fallback={
                    <div className="w-full min-h-[450px] flex items-center justify-center border-y border-amber-500/5 bg-[#080604]/40">
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <span className="text-xs text-amber-500/60 font-mono tracking-widest uppercase">در حال بازخوانی کتیبه‌های باستانی...</span>
                      </div>
                    </div>
                  }>
                    <ArticlesAndArchetypes
                      selectedArchetypeId={selectedArchetypeId}
                      onClearArchetype={handleClearArchetype}
                      isHomePreview={true}
                      onViewAll={() => navigate(localizedPath("articles"))}
                    />
                  </Suspense>

                  {/* Section 4: Archetypes Selector (Interactive Stone Tablets at the bottom) */}
                  <ArchetypeSelector
                    onArchetypeSelect={handleArchetypeSelect}
                    selectedArchetypeId={selectedArchetypeId}
                  />
                </motion.div>
              } />

              <Route path="/en" element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Hero />
                  <Suspense fallback={null}>
                    <YouTubeFeed isHomePreview={true} onViewAll={() => navigate(localizedPath("videos"))} />
                 </Suspense>
                  <Suspense fallback={null}>
                    <ArticlesAndArchetypes
                      selectedArchetypeId={selectedArchetypeId}
                      onClearArchetype={handleClearArchetype}
                      isHomePreview={true}
                      onViewAll={() => navigate(localizedPath("articles"))}
                    />
                 </Suspense>
                  <ArchetypeSelector
                    onArchetypeSelect={handleArchetypeSelect}
                    selectedArchetypeId={selectedArchetypeId}
                  />
               </motion.div>
              } />

              <Route path="/en/videos" element={
                <PageWrapper
                  title={t("videoTitle")}
                  subtitle={t("videoSubtitle")}
                  backTo="/en"
                >
                  <Suspense fallback={null}>
                    <YouTubeFeed isHomePreview={false} />
                 </Suspense>
               </PageWrapper>
              } />

              <Route path="/en/articles" element={
                <PageWrapper
                  title={t("articlesTitleAll")}
                  subtitle={t("articlesSubtitleAll")}
                  backTo="/en"
                >
                  <Suspense fallback={null}>
                    <ArticlesAndArchetypes
                      selectedArchetypeId={selectedArchetypeId}
                      onClearArchetype={handleClearArchetype}
                      isHomePreview={false}
                    />
                 </Suspense>
               </PageWrapper>
              } />

              <Route path="/en/articles/:slug" element={
                <PageWrapper
                  title={t("articlesTitleAll")}
                  subtitle={t("articlesSubtitleAll")}
                  backTo="/en/articles"
                  backLabel={isRtl ? "بازگشت به کتیبه‌ها" : "Back to Inscriptions"}
                >
                  <Suspense fallback={null}>
                    <ArticlesAndArchetypes
                      selectedArchetypeId={selectedArchetypeId}
                      onClearArchetype={handleClearArchetype}
                      isHomePreview={false}
                    />
                 </Suspense>
               </PageWrapper>
              } />

              <Route path="/en/quiz" element={
                <PageWrapper
                  title={t("navQuiz")}
                  backTo="/en"
                >
                  <ArchetypeQuiz
                    onBackToHall={() => navigate(localizedPath("home"))}
                    onNavigateToArticles={(archetypeId) => {
                      setSelectedArchetypeId(archetypeId);
                      navigate(localizedPath("articles"));
                    }}
                  />
               </PageWrapper>
              } />

              <Route path="/en/haftkhan" element={
                <PageWrapper
                  title={t("navHaftKhan")}
                  backTo="/en"
                >
                  <HaftKhanJourney />
               </PageWrapper>
              } />

              <Route path="/videos" element={
                <PageWrapper
                  title={t("videoTitle")}
                  subtitle={t("videoSubtitle")}
                  backTo="/"
                >
                  <Suspense fallback={
                    <div className="w-full min-h-[600px] flex items-center justify-center border-y border-amber-500/5 bg-[#080604]/40">
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <span className="text-xs text-amber-500/60 font-mono tracking-widest uppercase">در حال فراخوانی نگاره‌های ویدیویی...</span>
                      </div>
                    </div>
                  }>
                    <YouTubeFeed isHomePreview={false} />
                  </Suspense>
                </PageWrapper>
              } />

              <Route path="/articles" element={
                <PageWrapper
                  title={t("articlesTitleAll")}
                  subtitle={t("articlesSubtitleAll")}
                  backTo="/"
                >
                  <Suspense fallback={
                    <div className="w-full min-h-[600px] flex items-center justify-center border-y border-amber-500/5 bg-[#080604]/40">
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <span className="text-xs text-amber-500/60 font-mono tracking-widest uppercase">در حال بازخوانی کتیبه‌های باستانی...</span>
                      </div>
                    </div>
                  }>
                    <ArticlesAndArchetypes
                      selectedArchetypeId={selectedArchetypeId}
                      onClearArchetype={handleClearArchetype}
                      isHomePreview={false}
                    />
                  </Suspense>
                </PageWrapper>
              } />

              <Route path="/articles/:slug" element={
                <PageWrapper
                  title={t("articlesTitleAll")}
                  subtitle={t("articlesSubtitleAll")}
                  backTo="/articles"
                  backLabel={isRtl ? "بازگشت به کتیبه‌ها" : "Back to Inscriptions"}
                >
                  <Suspense fallback={
                    <div className="w-full min-h-[600px] flex items-center justify-center border-y border-amber-500/5 bg-[#080604]/40">
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <span className="text-xs text-amber-500/60 font-mono tracking-widest uppercase">در حال بازخوانی کتیبه‌های باستانی...</span>
                      </div>
                    </div>
                  }>
                    <ArticlesAndArchetypes
                      selectedArchetypeId={selectedArchetypeId}
                      onClearArchetype={handleClearArchetype}
                      isHomePreview={false}
                    />
                  </Suspense>
                </PageWrapper>
              } />

              <Route path="/quiz" element={
                <PageWrapper
                  title={t("navQuiz")}
                  backTo="/"
                >
                  <ArchetypeQuiz
                    onBackToHall={() => navigate(localizedPath("home"))}
                    onNavigateToArticles={(archetypeId) => {
                      setSelectedArchetypeId(archetypeId);
                      navigate(localizedPath("articles"));
                    }}
                  />
                </PageWrapper>
              } />

              <Route path="/haftkhan" element={
                <PageWrapper
                  title={t("navHaftKhan")}
                  backTo="/"
                >
                  <HaftKhanJourney />
                </PageWrapper>
              } />

              {/* Catch-all: unknown URLs (rewritten to /index.html by the Pages
                  SPA fallback) render a real bilingual 404 hall, never blank. */}
              <Route path="*" element={
                <PageWrapper
                  title={isRtl ? "یافت نشد" : "Not Found"}
                  backTo="/"
                >
                  <NotFound />
                </PageWrapper>
              } />
            </Routes>
          </Suspense>
        </main>

        {/* Section 4: Royal Footer */}
        <RoyalFooter onNavigate={(view) => {
          const target = view === "home" ? "/" : `/${view}`;
          navigate(lang === "en" ? (target === "/" ? "/en" : `/en${target}`) : target);
        }} />

        <AnimatePresence>
          {isHafezOpen && (
            <div className="fixed inset-0 z-50 overflow-y-auto bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-4xl bg-[#0a0704] rounded-2xl overflow-hidden shadow-[0_0_80px_rgba(212,175,55,0.25)] border border-[#d4af37]/25 relative"
              >
                <Suspense fallback={
                  <div className="p-12 text-center text-amber-200">
                    <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <span className="font-sans text-xs">در حال گشودن دیوان حافظ...</span>
                  </div>
                }>
                  <HafezOracle onClose={() => setIsHafezOpen(false)} />
                </Suspense>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}